import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/auth/apiClient';
import { queryKeys } from './queryKeys';
import type { Katalogansicht, UUID } from './types';

/** Katalog und Plan-Positionen zusammengefuehrt, mit Zwischensummen je
 *  Gruppe — siehe GET /plan/{planId}/sachkosten/katalogansicht. */
export function useKatalogansicht(planId: UUID | null) {
  return useQuery({
    queryKey: planId ? queryKeys.sach.katalogansicht(planId) : ['sach', 'katalogansicht', 'none'],
    queryFn: async (): Promise<Katalogansicht> => {
      if (!planId) return { gruppen: [], summe_jahr: '0' };
      const res = await apiClient.get<Katalogansicht>(
        `/plan/${planId}/sachkosten/katalogansicht`,
      );
      return res.data;
    },
    enabled: !!planId,
  });
}

/**
 * Trägerweite Umbenennung eines Sachkonto-Codes ("dauerhaft für alle
 * Pläne", Task 7) — PUT /traeger/{traegerId}/sachkonto-label/{kontoCode}.
 * `traegerId` ist erst bekannt, nachdem Plan → Einrichtung → Träger
 * aufgelöst wurden; bis dahin schlägt ein Aufruf bewusst fehl statt
 * still nichts zu tun.
 *
 * Wirkt trägerweit, also über den aktuell offenen Plan hinaus: Ein anderer
 * Plan desselben Trägers, den der Nutzer in derselben Sitzung schon offen
 * hatte, hat seine Katalogansicht bereits gecacht. Ohne Invalidierung des
 * gesamten Sachkosten-Bereichs (`queryKeys.sach.all`) zeigte dieser Plan
 * beim Zurückwechseln weiter die alte Bezeichnung, obwohl "dauerhaft"
 * gewählt wurde.
 */
export function useSetSachkontoLabel(traegerId: UUID | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { kontoCode: string; bezeichnung: string }): Promise<void> => {
      if (!traegerId) throw new Error('traegerId unbekannt');
      await apiClient.put(`/traeger/${traegerId}/sachkonto-label/${vars.kontoCode}`, {
        bezeichnung: vars.bezeichnung,
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: queryKeys.sach.all });
    },
  });
}

// Kein `useResetSachkontoLabel` (DELETE .../sachkonto-label/{kontoCode}):
// Die "Auf Standard zurücksetzen"-Aktion dieser Ansicht setzt ausschließlich
// die Plan-Ausnahme (`bezeichnung_override`) zurück, nie die trägerweite
// Umbenennung selbst — dafür gibt es hier keine spezifizierte Aktion. Bei
// Bedarf lässt sich der Hook nach demselben Muster wie oben ergänzen.
